import { Clear } from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { typingTimeOut } from '../../../constants';
import { TalentStatus } from '../../../enums/talentStatus';
import { TalentType } from '../../../enums/talentType';
import { Talent } from '../../../models/talent';
import { talentService } from '../../../services/talentService';

export interface FilterTalentsForm {
  talents: Talent[];
  accountType: string;
  status: string[];
  awPayrollId: string;
  startDate: null;
  endDate: null;
}

interface Props {
  handleFormSubmit: (data: FilterTalentsForm) => void;
  filterData: FilterTalentsForm;
}

const defaultValues: FilterTalentsForm = {
  talents: [],
  accountType: '',
  status: [],
  awPayrollId: '',
  startDate: null,
  endDate: null
};

const AdminFilterTalents = (props: Props): JSX.Element => {
  const [filterData, setFilterData] = useState<FilterTalentsForm>(
    props.filterData
  );
  const [talents, setTalents] = useState<Talent[]>([]);

  useEffect(() => {
    getTalents();
  }, []);

  const getTalents = () => {
    talentService.getTalentList().then((response) => {
      setTalents(response.data);
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string[]> | SelectChangeEvent
  ) => {
    const {
      target: { value, name }
    } = event;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              multiple
              id="talent"
              options={talents}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              value={filterData.talents ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  talents: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Talent" />}
            />
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="accountType">Type</InputLabel>
              <Select
                labelId="accountType"
                label="accountType"
                name="accountType"
                value={filterData.accountType}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Type" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.keys(TalentType).map((talentType) => (
                  <MenuItem key={talentType} value={talentType}>
                    {TalentType[talentType as keyof typeof TalentType]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="status">Status</InputLabel>
              <Select
                labelId="status"
                label="status"
                multiple
                name="status"
                value={filterData.status}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected: string[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} sx={{ height: 'auto' }} />
                    ))}
                  </Box>
                )}
              >
                {Object.keys(TalentStatus).map((statusKey) => (
                  <MenuItem key={statusKey} value={statusKey}>
                    {TalentStatus[statusKey as keyof typeof TalentStatus]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ maxWidth: 245 }}
              label="AW Payroll ID"
              name="awPayrollId"
              value={filterData.awPayrollId}
              onChange={handleChange}
            />
            <DatePicker
              label="Start Date"
              value={filterData.startDate}
              onChange={(date) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  startDate: date
                }));
              }}
              maxDate={filterData.endDate}
              renderInput={(params) => (
                <TextField {...params} sx={{ maxWidth: 245 }} />
              )}
            />
            <DatePicker
              label="End Date"
              value={filterData.endDate}
              onChange={(date) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  endDate: date
                }));
              }}
              minDate={filterData.startDate}
              renderInput={(params) => (
                <TextField {...params} sx={{ maxWidth: 245 }} />
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default AdminFilterTalents;
